import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';
import { dataTableEvents } from 'src/app/core/constants/data-table-events';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { EventsServiceAction, EventsServiceData } from 'src/app/core/services/base-event-service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { isPresent } from 'src/app/core/utils/isPresent';
import { DialogsActions } from 'src/app/modules/dialogs/store/actions';
import { DocSendReportModalComponent } from 'src/app/modules/documents/components/modals/doc-send-report-modal/doc-send-report-modal.component';
import { DocumentsApiService } from 'src/app/modules/documents/services/documents-api.service';
import { DocumentsActions } from 'src/app/modules/documents/store/actions';
import { DataTableItem } from 'src/app/shared/components/data-table/client-side-data-table/client-side-data-table.component';
import { DateShortPipe } from 'src/app/shared/pipes/date-short.pipe';
import { ThreeDotsMenuPipe } from 'src/app/shared/pipes/three-dots-menu.pipe';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { environment } from 'src/environment/environment';
import { WorkspaceDetailsDto, WorkspaceDocumentDto } from '../../models/workspaces.model';
import { WorkspacesApiService } from '../../services/workspaces-api.service';
import { WorkspacesActions } from '../../store/actions';
import { selectWorkspaceDetails, selectWorkspaceDetailsLoading } from '../../store/selectors';
import { WorkspaceDocumentsEventsService } from './workspace-documents-events.service';

@UntilDestroy()
@Component({
  selector: 'app-workspace-documents',
  templateUrl: './workspace-documents.component.html',
  styleUrl: './workspace-documents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceDocumentsComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public workspaceId: string | null = null;
  public dataRecieved: boolean = false;
  public isWorkspaceDetailsLoading$ = this.store$.select(selectWorkspaceDetailsLoading);
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public data: WorkspaceDocumentDto[] = [];
  public columns!: DataTables.ColumnSettings[];
  public sort!: (string | number)[][];
  public dataTableMenuItems!: DataTableItem[];
  public refreshDataTable: BehaviorSubject<any> = new BehaviorSubject(null);
  private canWriteWorkspaces = false;
  private canWriteDocuments = false;
  private canDeleteDocuments = false;
  private canReadReporting = false;
  private canReadDocumentProcessing = false;

  constructor(
    private readonly store$: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly translocoService: TranslocoService,
    public workspaceDocumentsEventsService: WorkspaceDocumentsEventsService,
    public threeDotsMenuPipe: ThreeDotsMenuPipe,
    private readonly dateShortPipe: DateShortPipe,
    private readonly bsModalService: BsModalService,
    private readonly documentsApiService: DocumentsApiService,
    private readonly workspacesApiService: WorkspacesApiService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.workspaceId = this.route.snapshot.paramMap.get('id');
    this.initSubscriptions();
    this.startInitialization();
  }

  initSubscriptions(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.data) {
        this.initTable(this.data);
        this.refreshDataTable.next(this.columns);
      }
    });

    this.workspaceDocumentsEventsService
      .getSubject()
      .pipe(untilDestroyed(this))
      .subscribe((event: EventsServiceData) => {
        switch (event.action) {
          case EventsServiceAction.NameClick:
          case EventsServiceAction.ReviewLink: {
            this.openForReview(event.id);
            break;
          }
          case EventsServiceAction.SendReport: {
            this.sendReport(event.id);
            break;
          }
          case EventsServiceAction.Download: {
            this.downloadDocument(event.id);
            break;
          }
          case EventsServiceAction.Unlink: {
            if (!this.canWriteWorkspaces) {
              return;
            }

            this.unlinkFromWorkspace(event.id);
            break;
          }
          case EventsServiceAction.Delete: {
            if (!this.canDeleteDocuments) {
              return;
            }

            this.deleteDocument(event.id);
            break;
          }
        }
      });
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails).pipe(filter(isPresent)),
      this.permissionService.canWrite$(PermissionResource.Workspaces),
      this.permissionService.canWrite$(PermissionResource.Documents),
      this.permissionService.canDelete$(PermissionResource.Documents),
      this.permissionService.canRead$(PermissionResource.Reporting),
      this.permissionService.canRead$(PermissionResource.DocumentProcessing),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([
        data,
        canWriteWorkspaces,
        canWriteDocuments,
        canDeleteDocuments,
        canReadReporting,
        canReadDocumentProcessing,
      ]) => {
        this.canWriteWorkspaces = canWriteWorkspaces;
        this.canWriteDocuments = canWriteDocuments;
        this.canDeleteDocuments = canDeleteDocuments;
        this.canReadReporting = canReadReporting;
        this.canReadDocumentProcessing = canReadDocumentProcessing;
        this.workspaceDetails = data;
        this.initTable(data.documents || []);
        this.cdr.detectChanges();

        setTimeout(() => {
          this.refreshDataTable.next(this.columns);
          this.cdr.detectChanges();
        }, 1);

        this.cdr.markForCheck();
      });
  }

  initColumns(): void {
    const that = this;
    this.columns = Object.assign([
      {
        title: this.translocoService.translate('workspaces.document'),
        data: 'name',
        render: function (data: any, type: any, full: any) {
          return `<a name-click='${full.id}'>${data}</a>`;
        },
      },
      {
        title: this.translocoService.translate('workspaces.dateAdded'),
        data: 'addedAt',
        render: {
          display: function (data: any) {
            return that.dateShortPipe.transform(data);
          },
          sort: function (data: any) {
            return new Date(data).getTime();
          },
        },
      },
      {
        title: this.translocoService.translate('workspaces.action'),
        width: '50px',
        data: 'id',
        searchable: false,
        orderable: false,
        render: function (data: any) {
          return that.threeDotsMenuPipe.transform(data, that.dataTableMenuItems);
        },
      },
    ]);
  }

  initTable(data: WorkspaceDocumentDto[]): void {
    this.initColumns();
    this.dataTableMenuItems = [
      ...(this.canReadDocumentProcessing
        ? [
      {
        title: this.translocoService.translate('actions.openForReview'),
        event: dataTableEvents.common.reviewLink,
        icon: 'quick_reference_all',
      },
          ]
        : []),
      ...(this.canReadReporting
        ? [
      {
        title: this.translocoService.translate('actions.sendReport'),
        event: dataTableEvents.common.sendReport,
        icon: 'outgoing_mail',
      },
          ]
        : []),
      {
        title: this.translocoService.translate('actions.download'),
        event: dataTableEvents.common.download,
        icon: 'download',
      },
      ...(this.canWriteWorkspaces
        ? [
      {
        title: this.translocoService.translate('actions.unlinkFromWorkspace'),
        event: dataTableEvents.common.unlink,
        icon: 'link_off',
      },
          ]
        : []),
      ...(this.canDeleteDocuments
        ? [
      {
        title: this.translocoService.translate('actions.remove'),
        event: dataTableEvents.common.delete,
        icon: 'delete',
        disabled: false,
      },
          ]
        : []),
    ];
    this.sort = [[0, 'asc']];
    this.data = data || [];
  }

  openForReview(documentId: string): void {
    if (!this.canReadDocumentProcessing) {
      return;
    }

    window.open(`${environment.reviewServerUrl}/home?docGuid=${documentId}`, '_blank');
  }

  sendReport(documentId: string): void {
    if (!this.canReadReporting) {
      return;
    }

    this.documentsApiService
      .getDocumentById$(documentId)
      .pipe(untilDestroyed(this))
      .subscribe((document) => {
        this.bsModalService.show(DocSendReportModalComponent, {
          initialState: {
            document,
          },
          class: 'modal-lg',
        });
      });
  }

  downloadDocument(documentId: string): void {
    const fileName = this.data.find((item) => item.id === documentId)?.name || 'document.docx';
    this.store$.dispatch(DocumentsActions.downloadDocument({ uniqueId: documentId, fileName }));
  }

  unlinkFromWorkspace(documentId: string): void {
    if (!this.canWriteWorkspaces) {
      return;
    }

    if (!this.workspaceId) {
      return;
    }

    this.workspacesApiService
      .unlinkWorkspaceDocument$(this.workspaceId, documentId)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.store$.dispatch(WorkspacesActions.getWorkspaceDetails({ id: this.workspaceId! }));
      });
  }

  deleteDocument(documentId: string): void {
    if (!this.canDeleteDocuments) {
      return;
    }

    this.store$.dispatch(
      DialogsActions.showDialog({
        data: {
          actionsKeys: ['yes', 'no'],
          content: '',
          title: this.translocoService.translate('modalBody.confirmDeleteDocument'),
        },
        callback: (action) => {
          if (action !== 'yes') {
            return;
          }

          this.documentsApiService
            .deleteDocument$(documentId)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.store$.dispatch(DocumentsActions.getDocuments());
              if (this.workspaceId) {
                this.store$.dispatch(
                  WorkspacesActions.getWorkspaceDetails({ id: this.workspaceId }),
                );
              }
            });
        },
      }),
    );
  }

  get workspaceDocuments(): WorkspaceDocumentDto[] {
    return this.workspaceDetails?.documents || [];
  }
}
